import { useCallback, useSyncExternalStore } from "react";
import { db, onTablesChanged } from "./client";

/*
 * Rows looked up by id from many small components at once: a note's task, file and inline-tag blocks. Ids asked for
 * in the same moment go out as one query, and a change to the table re-reads the ids on screen in one query. Each
 * block used to run its own: a note with 120 tasks ran 120 queries when it opened and 120 more whenever any task
 * changed anywhere, and its 120 results pushed everything else out of useQuery's 150-entry cache.
 * Rows of blocks no longer on screen are kept (a note opened again paints at once) until their table changes.
 */
type Table = "tasks" | "attachments" | "tags";
type Row = Record<string, unknown>;
/** row: undefined while loading, null when there is no such row. */
type Entry = { row: Row | null | undefined; loading: boolean; listeners: Set<() => void> };

const KEEP_IDLE = 500;
const CHUNK = 500; // ids per query (SQLite's variable limit is far above)
const tables = new Map<Table, Map<string, Entry>>();
const asked = new Map<Table, Set<string>>();
let flushQueued = false;

function entries(t: Table): Map<string, Entry> {
  let m = tables.get(t);
  if (!m) {
    tables.set(t, (m = new Map()));
    watch(t, m);
  }
  return m;
}

function sameRow(a: Row | null | undefined, b: Row | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  for (const k in a) if (a[k] !== b[k]) return false;
  for (const k in b) if (!(k in a)) return false;
  return true;
}

async function read(t: Table, ids: string[]): Promise<void> {
  const m = entries(t);
  for (let i = 0; i < ids.length; i += CHUNK) {
    const part = ids.slice(i, i + CHUNK);
    let rows: Row[];
    try {
      rows = await db.query<Row>(`SELECT * FROM ${t} WHERE id IN (${part.map(() => "?").join(",")})`, part);
    } catch (err) {
      for (const id of part) if (m.get(id)) m.get(id)!.loading = false; // asked again by the next block or change
      throw err;
    }
    const byId = new Map(rows.map((r) => [r.id as string, r]));
    for (const id of part) {
      const e = m.get(id);
      if (!e) continue;
      e.loading = false;
      const row = byId.get(id) ?? null;
      if (sameRow(e.row, row)) continue;
      e.row = row;
      for (const l of e.listeners) l();
    }
  }
}

function ask(t: Table, id: string) {
  let s = asked.get(t);
  if (!s) asked.set(t, (s = new Set()));
  s.add(id);
  if (flushQueued) return;
  flushQueued = true;
  // Blocks subscribe in the same effects pass when a note opens: one query for all of them.
  queueMicrotask(() => {
    flushQueued = false;
    for (const [table, ids] of asked) {
      asked.delete(table);
      read(table, [...ids]).catch((e) => console.error(e));
    }
  });
}

/** On a change to the table: forget rows no block shows, re-read the rest (one run at a time, then once more). */
function watch(t: Table, m: Map<string, Entry>) {
  let busy = false;
  let again = false;
  const reload = () => {
    if (busy) return void (again = true);
    for (const [id, e] of m) if (!e.listeners.size) m.delete(id);
    const ids = [...m.keys()];
    if (!ids.length) return;
    busy = true;
    read(t, ids)
      .catch((e) => console.error(e))
      .finally(() => {
        busy = false;
        if (again) {
          again = false;
          reload();
        }
      });
  };
  onTablesChanged((changed) => changed.has(t) && reload());
}

function subscribe(t: Table, id: string, listener: () => void): () => void {
  const m = entries(t);
  let e = m.get(id);
  if (e) m.delete(id); // most recently used goes last
  else e = { row: undefined, loading: false, listeners: new Set() };
  m.set(id, e);
  e.listeners.add(listener);
  if (e.row === undefined && !e.loading) {
    e.loading = true;
    ask(t, id);
  }
  const entry = e;
  return () => {
    entry.listeners.delete(listener);
    if (m.size <= KEEP_IDLE) return;
    for (const [k, x] of m) {
      if (m.size <= KEEP_IDLE) break;
      if (!x.listeners.size && !x.loading) m.delete(k);
    }
  };
}

/** The row of `table` with this id (all columns, deleted rows included): undefined while loading, null if none. */
export function useRowById<T>(table: Table, id: string | null | undefined): T | null | undefined {
  const sub = useCallback((l: () => void) => (id ? subscribe(table, id, l) : () => {}), [table, id]);
  const get = useCallback(() => (id ? (tables.get(table)?.get(id)?.row as T | null | undefined) : null), [table, id]);
  return useSyncExternalStore(sub, get);
}
